import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Banknote,
  Building2,
  HandCoins,
  ShieldCheck,
  Mail,
  Heart,
  Loader2,
  ArrowRight,
} from "lucide-react";
import PageBanner from "../components/PageBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_AMOUNTS = [25, 60, 150, 500];
const FREQUENCIES = ["one-time", "monthly", "quarterly", "annually"];
const DESIGNATIONS = [
  "Where most needed",
  "Health Access & System Innovation",
  "Food Safety & Community Wellness",
  "Sustainable Energy & Infrastructure",
  "Future-Ready Education & Workforce",
  "Global Diaspora Connectivity",
];

const WAYS = [
  {
    icon: Banknote,
    title: "Direct Bank Transfer",
    text: "Send a one-time or recurring gift to our foundation account. Best for larger contributions and zero processing fees.",
    bullets: [
      "Bank: First Community Trust Bank",
      "Account name: TMB Global Foundation",
      "Account #: 4002-118-9931",
      "SWIFT: FCTBUS44",
    ],
  },
  {
    icon: Building2,
    title: "Corporate & Partner Giving",
    text: "Sponsor a program, match employee giving, or co-create a campaign with us. We tailor reporting to your CSR and ESG goals.",
    bullets: [
      "Program sponsorship",
      "Employee matching",
      "Co-branded campaigns",
      "Annual impact reports",
    ],
  },
  {
    icon: HandCoins,
    title: "In-Kind & Legacy Gifts",
    text: "Donate medical supplies, solar equipment, school materials, or include the Foundation in your estate plans for lasting impact.",
    bullets: [
      "Medical & school supplies",
      "Solar & energy equipment",
      "Bequests & legacy giving",
      "Stocks & securities",
    ],
  },
];

const IMPACT_TIERS = [
  { amount: "$25", text: "Powers a solar lighting kit for one classroom." },
  { amount: "$60", text: "Funds a community health screening session." },
  { amount: "$150", text: "Equips a Future Skills Academy seat for a quarter." },
  { amount: "$500", text: "Sponsors a Smart Community Hub pilot deployment." },
];

export default function Donate() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    amount: "",
    customSelected: false,
    frequency: "one-time",
    designation: "Where most needed",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    const amountNum = parseFloat(form.amount);
    if (!form.name || !form.email || !amountNum || amountNum <= 0) {
      toast.error("Please fill name, email and a donation amount greater than 0.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/donation`, {
        name: form.name,
        email: form.email,
        amount: amountNum,
        frequency: form.frequency,
        designation: form.designation,
        message: form.message,
      });
      toast.success(
        "Thank you! Your pledge is received. Our team will follow up from Donate@tmbglobal.news."
      );
      setForm({
        name: "",
        email: "",
        amount: "",
        customSelected: false,
        frequency: "one-time",
        designation: "Where most needed",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <PageBanner
        overline="Stand with us"
        title="Your gift powers connected systems."
        subtitle="Every contribution funds solar-equipped clinics, clean food networks, future-ready academies and the digital trust that makes them work."
        image="https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/9487f5669daf1b7a51022e9ccebdeab3778f6bf109027b9add948838a00b4953.png"
      />

      {/* Donate form */}
      <section className="section">
        <div className="container-tmb grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-6">
            <div>
              <div className="overline mb-3">Make a pledge</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05] mb-4">
                Give in a way that fits you.
              </h2>
              <p className="text-[#4A5D54] leading-relaxed">
                Submit a pledge below — our donations team will respond from{" "}
                <strong>Donate@tmbglobal.news</strong> with secure giving instructions
                and a receipt for your records.
              </p>
            </div>

            <div className="card-tmb p-7 space-y-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-[#2C4C3B]" />
                <div className="overline">Transparency</div>
              </div>
              <p className="text-sm text-[#4A5D54] leading-relaxed">
                Every dollar is accounted for. We publish program-level financials and
                independent impact assessments, and use blockchain-backed records to
                ensure transparent tracking of services and funding.
              </p>
            </div>

            <div className="card-tmb p-7 bg-[#2C4C3B] text-white border-transparent">
              <div className="text-[#D99A29] uppercase tracking-[0.2em] font-bold text-xs mb-3">
                Larger gifts & partnerships
              </div>
              <p className="text-white/85 text-sm leading-relaxed mb-4">
                Planning a major gift, sponsorship, or legacy contribution? Reach our
                team directly:
              </p>
              <a
                href="mailto:Donate@tmbglobal.news"
                data-testid="donate-email-link"
                className="inline-flex items-center gap-2 text-white hover:text-[#D99A29]"
              >
                <Mail className="w-4 h-4" /> Donate@tmbglobal.news
              </a>
            </div>
          </div>

          <form
            data-testid="donation-form"
            onSubmit={submit}
            className="md:col-span-7 card-tmb p-8 md:p-10"
          >
            <div className="overline mb-3">Pledge form</div>
            <h3 className="font-display text-3xl md:text-4xl text-[#1A2421] tracking-tight leading-tight mb-2">
              Donate to TMB Global Foundation.
            </h3>
            <p className="text-sm text-[#4A5D54] mb-7">
              Pledges are routed to <strong>Donate@tmbglobal.news</strong>.
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label
                  htmlFor="dname"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Your name
                </label>
                <input
                  id="dname"
                  type="text"
                  className="input-tmb"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  data-testid="donation-input-name"
                />
              </div>
              <div>
                <label
                  htmlFor="demail"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Email
                </label>
                <input
                  id="demail"
                  type="email"
                  className="input-tmb"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  data-testid="donation-input-email"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-3">
                Donation amount (USD)
              </label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {QUICK_AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    data-testid={`donation-quick-${a}`}
                    onClick={() =>
                      setForm({ ...form, amount: String(a), customSelected: false })
                    }
                    className={`py-3 rounded-xl border font-semibold transition-all ${
                      form.amount === String(a) && !form.customSelected
                        ? "bg-[#C85A3A] text-white border-[#C85A3A]"
                        : "bg-white text-[#1A2421] border-[#C85A3A]/20 hover:border-[#C85A3A]"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                step="1"
                className="input-tmb"
                value={form.customSelected ? form.amount : form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value, customSelected: true })
                }
                placeholder="Or enter a custom amount"
                data-testid="donation-input-amount"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label
                  htmlFor="freq"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Frequency
                </label>
                <select
                  id="freq"
                  className="input-tmb appearance-none"
                  value={form.frequency}
                  onChange={update("frequency")}
                  data-testid="donation-input-frequency"
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="desig"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Direct my gift to
                </label>
                <select
                  id="desig"
                  className="input-tmb appearance-none"
                  value={form.designation}
                  onChange={update("designation")}
                  data-testid="donation-input-designation"
                >
                  {DESIGNATIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-7">
              <label
                htmlFor="dmsg"
                className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
              >
                Message (optional)
              </label>
              <textarea
                id="dmsg"
                rows={4}
                className="input-tmb resize-none"
                value={form.message}
                onChange={update("message")}
                placeholder="Share what's moving you to give, or any notes for our team."
                data-testid="donation-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="donation-submit-button"
              className="btn-primary disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Heart className="w-4 h-4" /> Submit my pledge
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Impact tiers */}
      <section className="section bg-[#F0EBE1]">
        <div className="container-tmb">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-4">Your impact</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              Small gifts. Connected, measurable change.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {IMPACT_TIERS.map((t, i) => (
              <motion.div
                key={t.amount}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="card-tmb p-7"
                data-testid={`impact-tier-${i}`}
              >
                <div className="font-display text-4xl md:text-5xl text-[#C85A3A] leading-none mb-3">
                  {t.amount}
                </div>
                <p className="text-sm text-[#4A5D54] leading-relaxed">{t.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ways to give */}
      <section className="section">
        <div className="container-tmb">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-4">Other ways to give</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              More paths to the same mission.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WAYS.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="card-tmb p-8 flex flex-col"
                  data-testid={`way-to-give-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#C85A3A]" />
                  </div>
                  <h3 className="font-display text-2xl text-[#1A2421] mb-3">
                    {w.title}
                  </h3>
                  <p className="text-sm text-[#4A5D54] leading-relaxed mb-5">
                    {w.text}
                  </p>
                  <ul className="space-y-2 text-sm text-[#1A2421] border-t border-black/5 pt-4 mt-auto">
                    {w.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span className="text-[#C85A3A] mt-1">·</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/contact" data-testid="donate-contact-cta" className="btn-ghost">
              Have a question? Contact us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
